/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo } from "react";
import { ProductCard } from "@/components/atoms";
import { Button } from "@/components/ui";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Grid, Search, X, ChevronDown, ChevronRight, Sliders, List, Loader2 } from "lucide-react";
import {
  useGetAllCategories,
  useGetAllSubCategories,
  useGetAllSubSubCategories,
} from "@/api/queries/use-get-categories";
import { useGetAllBrands } from "@/api/queries";
import { formatPrice, getProductAvailability } from "@/lib/utils";
import { useGetAllProducts } from "@/api/queries";

export const Shop = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams<{ categoryId?: string }>();
  const location = useLocation();

  // States
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategoryId, setActiveCategoryId] = useState(categoryId || "all");
  const [activeSubCategoryId, setActiveSubCategoryId] = useState<string | null>(null);
  const [activeSubSubCategoryId, setActiveSubSubCategoryId] = useState<string | null>(null);
  const [activeBrandId, setActiveBrandId] = useState<string | null>(null);
  const [displayLayout, setDisplayLayout] = useState<"grid" | "list">("grid");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<string>("featured");

  // State to track expanded categories and subcategories
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  // State to track if brands/categories sections are open
  const [isBrandsSectionOpen, setIsBrandsSectionOpen] = useState(true);
  const [isCategoriesSectionOpen, setIsCategoriesSectionOpen] = useState(true);

  // Extract parameters from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSearchQuery = searchParams.get("search") || "";
    const urlBrandId = searchParams.get("brand") || null;

    if (urlSearchQuery) {
      setSearchQuery(urlSearchQuery);
    }

    if (urlBrandId) {
      setActiveBrandId(urlBrandId);
    }
  }, [location.search]);

  // Prepare filters for API
  const filters = useMemo(() => {
    const apiFilters: any = {};

    if (searchQuery) {
      apiFilters.search = searchQuery;
    }

    if (activeCategoryId !== "all") {
      apiFilters.categoryId = activeCategoryId;
    }

    if (activeSubCategoryId) {
      apiFilters.subcategoryId = activeSubCategoryId;
    }

    if (activeSubSubCategoryId) {
      apiFilters.subsubcategoryId = activeSubSubCategoryId;
    }

    if (activeBrandId) {
      apiFilters.brandId = activeBrandId;
    }

    // Map sort order to API format
    switch (sortOrder) {
      case "price-low":
        apiFilters.sortBy = "finalPrice";
        apiFilters.sortOrder = "asc";
        break;
      case "price-high":
        apiFilters.sortBy = "finalPrice";
        apiFilters.sortOrder = "desc";
        break;
      case "name-asc":
        apiFilters.sortBy = "name";
        apiFilters.sortOrder = "asc";
        break;
      case "name-desc":
        apiFilters.sortBy = "name";
        apiFilters.sortOrder = "desc";
        break;
      default:
        break;
    }

    return apiFilters;
  }, [
    searchQuery,
    activeCategoryId,
    activeSubCategoryId,
    activeSubSubCategoryId,
    activeBrandId,
    sortOrder,
  ]);

  // Fetch products with filters
  const {
    data: productsData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useGetAllProducts(filters);

  // Fetch categories and brands
  const { data: categoriesData, isLoading: categoriesLoading } = useGetAllCategories();
  const { data: brandsData, isLoading: brandsLoading } = useGetAllBrands();

  const { data: subCategoriesData, isLoading: subCategoriesLoading } = useGetAllSubCategories(
    activeCategoryId !== "all" ? activeCategoryId : "",
  );

  const { data: subSubCategoriesData, isLoading: subSubCategoriesLoading } =
    useGetAllSubSubCategories(activeSubCategoryId || "");

  // Flatten all products from all pages
  const allProducts = useMemo(() => {
    if (!productsData?.pages) return [];
    return productsData.pages.flatMap((page) => page.data.data.products);
  }, [productsData]);

  const totalCount = productsData?.pages?.[0]?.data?.data?.pagination?.total || 0;

  // Handle brand change
  const handleBrandChange = (brandId: string | null) => {
    setActiveBrandId(brandId);

    const searchParams = new URLSearchParams(location.search);
    if (brandId) {
      searchParams.set("brand", brandId);
    } else {
      searchParams.delete("brand");
    }

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
    );
  };

  // Handle category change
  const handleCategoryChange = (catId: string) => {
    setActiveCategoryId(catId);
    setActiveSubCategoryId(null);
    setActiveSubSubCategoryId(null);

    setExpandedCategories((prev) => ({
      ...prev,
      [catId]: !prev[catId],
    }));

    const searchParams = new URLSearchParams(location.search);
    if (catId === "all") {
      navigate({
        pathname: "/shop",
        search: searchParams.toString(),
      });
    } else {
      navigate({
        pathname: `/shop/category/${catId}`,
        search: searchParams.toString(),
      });
    }
  };

  const handleSubCategoryChange = (subCatId: string) => {
    setActiveSubCategoryId(subCatId);
    setActiveSubSubCategoryId(null);

    setExpandedCategories((prev) => ({
      ...prev,
      [subCatId]: !prev[subCatId],
    }));

    const searchParams = new URLSearchParams(location.search);
    navigate({
      pathname: `/shop/category/${activeCategoryId}`,
      search:
        searchParams.toString() + (searchParams.toString() ? "&" : "") + `subcategory=${subCatId}`,
    });
  };

  const handleSubSubCategoryChange = (subSubCatId: string) => {
    setActiveSubSubCategoryId(subSubCatId);

    const searchParams = new URLSearchParams(location.search);
    navigate({
      pathname: `/shop/category/${activeCategoryId}`,
      search:
        searchParams.toString() +
        (searchParams.toString() ? "&" : "") +
        `subcategory=${activeSubCategoryId}&subsubcategory=${subSubCatId}`,
    });
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);

    const searchParams = new URLSearchParams(location.search);
    if (query) {
      searchParams.set("search", query);
    } else {
      searchParams.delete("search");
    }

    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
    );
  };

  const handleAddToCart = (productId: number) => {
    const product = allProducts.find((p) => p.id === productId);
    if (product) {
      navigate(`/product/${productId}-${product.sku}`);
    }
  };

  const resetFilters = () => {
    setSearchQuery("");
    setActiveCategoryId("all");
    setActiveSubCategoryId(null);
    setActiveSubSubCategoryId(null);
    setActiveBrandId(null);
    setSortOrder("featured");
    setExpandedCategories({});
    navigate("/shop");
  };

  // Get active brand name for display
  const activeBrandName = useMemo(() => {
    if (!activeBrandId || !brandsData?.data) return null;
    const brand = brandsData.data.find((b) => b.id === activeBrandId);
    return brand?.name;
  }, [activeBrandId, brandsData]);

  // Update active filters from URL params on initial load
  useEffect(() => {
    if (categoryId) {
      setActiveCategoryId(categoryId);
      setExpandedCategories((prev) => ({ ...prev, [categoryId]: true }));
    }

    const searchParams = new URLSearchParams(location.search);
    const subcategoryParam = searchParams.get("subcategory");
    const subsubcategoryParam = searchParams.get("subsubcategory");

    if (subcategoryParam) {
      setActiveSubCategoryId(subcategoryParam);
      setExpandedCategories((prev) => ({ ...prev, [subcategoryParam]: true }));
    }

    if (subsubcategoryParam) {
      setActiveSubSubCategoryId(subsubcategoryParam);
    }
  }, [categoryId, location.search]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [
    categoryId,
    location.search,
    activeCategoryId,
    activeSubCategoryId,
    activeSubSubCategoryId,
    activeBrandId,
  ]);

  if (error) {
    return (
      <div className='bg-gray-50 min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <h3 className='text-lg font-semibold text-red-600 mb-2'>Error loading products</h3>
          <p className='text-gray-600 mb-4'>Please try again later</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-gray-50 min-h-screen'>
      <div className='container mx-auto px-4 py-12'>
        <div className='flex flex-col lg:flex-row gap-8'>
          {/* Sidebar Filters - Desktop */}
          <div className='hidden lg:block w-64 shrink-0'>
            <div className='bg-white rounded-lg shadow-sm p-6 sticky top-24 space-y-8'>
              {/* Brands Filter */}
              <div>
                <div className='flex items-center justify-between mb-4'>
                  <h3 className='text-lg font-semibold'>Brands</h3>
                  <button
                    onClick={() => setIsBrandsSectionOpen(!isBrandsSectionOpen)}
                    className='p-1 hover:bg-gray-100 rounded'
                    aria-label='Toggle brands'
                  >
                    <ChevronDown
                      className={`h-5 w-5 transition-transform ${
                        isBrandsSectionOpen ? "" : "-rotate-90"
                      }`}
                    />
                  </button>
                </div>
                {isBrandsSectionOpen && (
                  <div className='space-y-2 max-h-64 overflow-y-auto'>
                    <button
                      onClick={() => handleBrandChange(null)}
                      className={`block w-full text-left px-3 py-2 rounded-md transition ${
                        !activeBrandId ? "bg-primary text-white" : "hover:bg-gray-100"
                      }`}
                    >
                      All Brands
                    </button>
                    {brandsLoading ? (
                      <div className='flex justify-center py-2'>
                        <Loader2 className='h-4 w-4 animate-spin text-primary' />
                      </div>
                    ) : (
                      brandsData?.data?.map((brand) => (
                        <button
                          key={brand.id}
                          onClick={() => handleBrandChange(brand.id)}
                          className={`block w-full text-left px-3 py-2 rounded-md transition ${
                            activeBrandId === brand.id
                              ? "bg-primary text-white"
                              : "hover:bg-gray-100"
                          }`}
                        >
                          {brand.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Categories Filter */}
              <div>
                <div className='flex items-center justify-between mb-4'>
                  <h3 className='text-lg font-semibold'>Categories</h3>
                  <button
                    onClick={() => setIsCategoriesSectionOpen(!isCategoriesSectionOpen)}
                    className='p-1 hover:bg-gray-100 rounded'
                    aria-label='Toggle categories'
                  >
                    <ChevronDown
                      className={`h-5 w-5 transition-transform ${
                        isCategoriesSectionOpen ? "" : "-rotate-90"
                      }`}
                    />
                  </button>
                </div>
                {isCategoriesSectionOpen && (
                  <div className='space-y-2'>
                    <button
                      onClick={() => handleCategoryChange("all")}
                      className={`block w-full text-left px-3 py-2 rounded-md transition ${
                        activeCategoryId === "all" ? "bg-primary text-white" : "hover:bg-gray-100"
                      }`}
                    >
                      All Products
                    </button>
                    {!categoriesLoading &&
                      categoriesData?.data?.map((category) => (
                        <div key={category.id} className='space-y-1'>
                          <div className='flex items-center justify-between'>
                            <button
                              onClick={() => handleCategoryChange(category.id)}
                              className={`flex-grow text-left px-3 py-2 rounded-md transition ${
                                activeCategoryId === category.id
                                  ? "bg-primary text-white"
                                  : "hover:bg-gray-100"
                              }`}
                            >
                              {category.name} ({category.count})
                            </button>
                            <button
                              onClick={() => handleCategoryChange(category.id)}
                              className='px-2 text-gray-500'
                            >
                              <ChevronRight
                                className={`h-4 w-4 transition-transform ${
                                  expandedCategories[category.id] ? "rotate-90" : ""
                                }`}
                              />
                            </button>
                          </div>

                          {expandedCategories[category.id] && activeCategoryId === category.id && (
                            <div className='ml-4 space-y-1'>
                              {subCategoriesLoading ? (
                                <div className='flex justify-center py-2'>
                                  <Loader2 className='h-4 w-4 animate-spin text-primary' />
                                </div>
                              ) : !subCategoriesData?.data?.length ? (
                                <div className='text-xs text-gray-500 px-3 py-1'>
                                  No subcategories
                                </div>
                              ) : (
                                subCategoriesData?.data?.map((subcategory) => (
                                  <div key={subcategory.id} className='space-y-1'>
                                    <div className='flex items-center justify-between'>
                                      <button
                                        onClick={() => handleSubCategoryChange(subcategory.id)}
                                        className={`flex-grow text-left px-3 py-1 rounded-md transition ${
                                          activeSubCategoryId === subcategory.id
                                            ? "bg-primary/80 text-white"
                                            : "hover:bg-gray-100"
                                        }`}
                                      >
                                        {subcategory.name}
                                      </button>
                                      <button
                                        onClick={() => handleSubCategoryChange(subcategory.id)}
                                        className='px-2 text-gray-500'
                                      >
                                        <ChevronRight
                                          className={`h-4 w-4 transition-transform ${
                                            expandedCategories[subcategory.id] ? "rotate-90" : ""
                                          }`}
                                        />
                                      </button>
                                    </div>

                                    {expandedCategories[subcategory.id] &&
                                      activeSubCategoryId === subcategory.id && (
                                        <div className='ml-4 space-y-1'>
                                          {subSubCategoriesLoading ? (
                                            <div className='flex justify-center py-2'>
                                              <Loader2 className='h-4 w-4 animate-spin text-primary' />
                                            </div>
                                          ) : !subSubCategoriesData?.data?.length ? (
                                            <div className='text-xs text-gray-500 px-3 py-1'>
                                              No sub-subcategories
                                            </div>
                                          ) : (
                                            subSubCategoriesData?.data?.map((subsubcategory) => (
                                              <button
                                                key={subsubcategory.id}
                                                onClick={() =>
                                                  handleSubSubCategoryChange(subsubcategory.id)
                                                }
                                                className={`block w-full text-left px-3 py-1 rounded-md transition ${
                                                  activeSubSubCategoryId === subsubcategory.id
                                                    ? "bg-primary/60 text-white"
                                                    : "hover:bg-gray-100"
                                                }`}
                                              >
                                                {subsubcategory.name}
                                              </button>
                                            ))
                                          )}
                                        </div>
                                      )}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className='flex-1'>
            {/* Brand Header (if brand is selected) */}
            {activeBrandName && (
              <div className='bg-white rounded-lg shadow-sm p-6 mb-6'>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='text-sm text-gray-500 mb-1'>Browsing</p>
                    <h1 className='text-2xl font-bold text-gray-900'>{activeBrandName} Products</h1>
                  </div>
                  <Button variant='outline' size='sm' onClick={() => handleBrandChange(null)}>
                    Clear Brand
                  </Button>
                </div>
              </div>
            )}

            {/* Controls Bar */}
            <div className='bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-wrap items-center justify-between gap-4'>
              <div className='relative flex-grow max-w-md'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <Search size={18} className='text-gray-400' />
                </div>
                <input
                  type='text'
                  placeholder='Search products...'
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className='pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary focus:border-primary'
                />
              </div>

              <button
                onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                className='lg:hidden flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md'
              >
                <Sliders size={18} />
                <span>Filters</span>
              </button>

              <div className='flex items-center space-x-4'>
                <div className='relative'>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className='pl-4 pr-10 py-2 border border-gray-300 rounded-md appearance-none focus:ring-primary focus:border-primary bg-white'
                  >
                    <option value='featured'>Featured</option>
                    <option value='price-low'>Price: Low to High</option>
                    <option value='price-high'>Price: High to Low</option>
                    <option value='name-asc'>Name: A to Z</option>
                    <option value='name-desc'>Name: Z to A</option>
                  </select>
                  <div className='absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none'>
                    <ChevronDown size={18} className='text-gray-500' />
                  </div>
                </div>

                <div className='hidden md:flex items-center gap-2 border border-gray-300 rounded-md overflow-hidden'>
                  <button
                    onClick={() => setDisplayLayout("grid")}
                    className={`p-2 ${displayLayout === "grid" ? "bg-gray-100" : "bg-white"}`}
                    aria-label='Grid view'
                  >
                    <Grid size={18} />
                  </button>
                  <button
                    onClick={() => setDisplayLayout("list")}
                    className={`p-2 ${displayLayout === "list" ? "bg-gray-100" : "bg-white"}`}
                    aria-label='List view'
                  >
                    <List size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Mobile Filters */}
            {mobileFiltersOpen && (
              <div className='lg:hidden bg-white rounded-lg shadow-sm p-6 mb-6 space-y-6'>
                <div className='flex justify-between items-center mb-4'>
                  <h3 className='text-lg font-semibold'>Filters</h3>
                  <button onClick={() => setMobileFiltersOpen(false)}>
                    <X size={20} />
                  </button>
                </div>

                {/* Mobile Brands */}
                <div>
                  <h3 className='text-md font-semibold mb-3'>Brands</h3>
                  <div className='space-y-2 max-h-48 overflow-y-auto'>
                    <button
                      onClick={() => handleBrandChange(null)}
                      className={`block w-full text-left px-3 py-2 rounded-md text-sm ${
                        !activeBrandId ? "bg-primary text-white" : "bg-gray-100 hover:bg-gray-200"
                      }`}
                    >
                      All Brands
                    </button>
                    {!brandsLoading &&
                      brandsData?.data?.map((brand) => (
                        <button
                          key={brand.id}
                          onClick={() => handleBrandChange(brand.id)}
                          className={`block w-full text-left px-3 py-2 rounded-md text-sm ${
                            activeBrandId === brand.id
                              ? "bg-primary text-white"
                              : "bg-gray-100 hover:bg-gray-200"
                          }`}
                        >
                          {brand.name}
                        </button>
                      ))}
                  </div>
                </div>

                {/* Mobile Categories (same as before) */}
                <div>
                  <h3 className='text-md font-semibold mb-3'>Categories</h3>
                  <div className='space-y-3'>
                    <button
                      onClick={() => handleCategoryChange("all")}
                      className={`block w-full text-left px-3 py-2 rounded-md text-sm ${
                        activeCategoryId === "all"
                          ? "bg-primary text-white"
                          : "bg-gray-100 hover:bg-gray-200"
                      }`}
                    >
                      All Products
                    </button>

                    {!categoriesLoading &&
                      categoriesData?.data?.map((category) => (
                        <div key={category.id} className='space-y-2'>
                          <div className='flex items-center justify-between'>
                            <button
                              onClick={() => handleCategoryChange(category.id)}
                              className={`flex-grow text-left px-3 py-2 rounded-md text-sm ${
                                activeCategoryId === category.id
                                  ? "bg-primary text-white"
                                  : "bg-gray-100 hover:bg-gray-200"
                              }`}
                            >
                              {category.name}
                            </button>
                            <button
                              onClick={() => handleCategoryChange(category.id)}
                              className={`px-2 ${
                                activeCategoryId === category.id ? "text-white" : "text-gray-500"
                              }`}
                            >
                              <ChevronRight
                                className={`h-4 w-4 transition-transform ${
                                  expandedCategories[category.id] ? "rotate-90" : ""
                                }`}
                              />
                            </button>
                          </div>

                          {expandedCategories[category.id] && activeCategoryId === category.id && (
                            <div className='ml-4 space-y-2'>
                              {subCategoriesLoading ? (
                                <div className='flex justify-center py-2'>
                                  <Loader2 className='h-4 w-4 animate-spin text-primary' />
                                </div>
                              ) : !subCategoriesData?.data?.length ? (
                                <div className='text-xs text-gray-500 px-3 py-1'>
                                  No subcategories
                                </div>
                              ) : (
                                subCategoriesData?.data?.map((subcategory) => (
                                  <div key={subcategory.id} className='space-y-2'>
                                    <div className='flex items-center justify-between'>
                                      <button
                                        onClick={() => handleSubCategoryChange(subcategory.id)}
                                        className={`flex-grow text-left px-3 py-1 rounded-md text-sm ${
                                          activeSubCategoryId === subcategory.id
                                            ? "bg-primary/80 text-white"
                                            : "bg-gray-100 hover:bg-gray-200"
                                        }`}
                                      >
                                        {subcategory.name}
                                      </button>
                                      <button
                                        onClick={() => handleSubCategoryChange(subcategory.id)}
                                        className={`px-2 ${
                                          activeSubCategoryId === subcategory.id
                                            ? "text-white"
                                            : "text-gray-500"
                                        }`}
                                      >
                                        <ChevronRight
                                          className={`h-4 w-4 transition-transform ${
                                            expandedCategories[subcategory.id] ? "rotate-90" : ""
                                          }`}
                                        />
                                      </button>
                                    </div>

                                    {expandedCategories[subcategory.id] &&
                                      activeSubCategoryId === subcategory.id && (
                                        <div className='ml-4 space-y-2'>
                                          {subSubCategoriesLoading ? (
                                            <div className='flex justify-center py-2'>
                                              <Loader2 className='h-4 w-4 animate-spin text-primary' />
                                            </div>
                                          ) : !subSubCategoriesData?.data?.length ? (
                                            <div className='text-xs text-gray-500 px-3 py-1'>
                                              No sub-subcategories
                                            </div>
                                          ) : (
                                            subSubCategoriesData?.data?.map((subsubcategory) => (
                                              <button
                                                key={subsubcategory.id}
                                                onClick={() =>
                                                  handleSubSubCategoryChange(subsubcategory.id)
                                                }
                                                className={`block w-full text-left px-3 py-1 rounded-md text-sm ${
                                                  activeSubSubCategoryId === subsubcategory.id
                                                    ? "bg-primary/60 text-white"
                                                    : "bg-gray-100 hover:bg-gray-200"
                                                }`}
                                              >
                                                {subsubcategory.name}
                                              </button>
                                            ))
                                          )}
                                        </div>
                                      )}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {/* Products Count */}
            <div className='text-gray-500 mb-6'>
              Showing {allProducts.length} of {totalCount} products
            </div>

            {/* Products Grid */}
            {isLoading && !allProducts.length ? (
              <div className='flex items-center justify-center p-12 bg-white rounded-lg shadow-sm'>
                <Loader2 className='h-8 w-8 animate-spin text-primary mr-2' />
                <span>Loading products...</span>
              </div>
            ) : allProducts.length > 0 ? (
              <div
                className={
                  displayLayout === "grid"
                    ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                    : "space-y-6"
                }
              >
                {allProducts?.map((product) => {
                  const { hasStock, isUnlimited } = getProductAvailability(product);

                  return (
                    <div
                      key={product.id}
                      className={
                        displayLayout === "list"
                          ? "bg-white rounded-lg shadow-sm overflow-hidden"
                          : ""
                      }
                    >
                      {displayLayout === "grid" ? (
                        <ProductCard
                          id={product.id}
                          name={product.name}
                          image={product.images?.[0]?.url || "/placeholder-image.jpg"}
                          price={product.finalPrice}
                          oldPrice={product.discountPercentage > 0 ? product.basePrice : undefined}
                          hasStock={hasStock}
                          isUnlimited={isUnlimited}
                          discount={product.discountPercentage}
                          sku={product.sku}
                        />
                      ) : (
                        <div className='flex flex-col md:flex-row'>
                          <div className='md:w-1/3 h-64 md:h-auto'>
                            <img
                              src={product.images?.[0]?.url || "/placeholder-image.jpg"}
                              alt={product.name}
                              className='w-full h-full object-cover'
                            />
                          </div>
                          <div className='p-6 flex-1 flex flex-col'>
                            <div className='mb-2'>
                              <span className='text-sm text-primary font-medium'>
                                {product.category?.name}
                              </span>
                            </div>
                            <h3 className='text-xl font-bold mb-2'>{product.name}</h3>
                            <p className='text-gray-600 mb-4 line-clamp-3'>{product.description}</p>
                            <div className='mt-auto flex flex-wrap items-end justify-between gap-4'>
                              <div>
                                <div className='flex items-baseline gap-2 mb-1'>
                                  <span className='text-2xl font-bold text-gray-900'>
                                    {formatPrice(parseInt(product.finalPrice))}
                                  </span>
                                  {product.discountPercentage > 0 && (
                                    <span className='text-gray-500 text-sm line-through'>
                                      {formatPrice(parseInt(product.basePrice))}
                                    </span>
                                  )}
                                </div>
                                <div className='text-sm text-gray-500'>
                                  {product.totalStock > 0
                                    ? `${product.totalStock} in stock`
                                    : "Out of stock"}
                                </div>
                              </div>
                              <Button
                                onClick={() => handleAddToCart(product.id)}
                                disabled={product.totalStock === 0}
                                className={`w-full ${
                                  !hasStock
                                    ? "bg-gray-200 hover:bg-gray-200 cursor-not-allowed text-black"
                                    : ""
                                }`}
                              >
                                {hasStock ? "View Details" : "Out of Stock"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className='text-center py-16 bg-white rounded-lg shadow-sm'>
                <div className='text-6xl mb-4'>🔍</div>
                <h3 className='text-2xl font-bold mb-2'>No products found</h3>
                <p className='text-gray-600 mb-6'>Try adjusting your search or filter criteria</p>
                <Button onClick={resetFilters}>Clear All Filters</Button>
              </div>
            )}

            {/* Load More Button */}
            {hasNextPage && (
              <div className='mt-12 text-center'>
                <Button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  variant='outline'
                  size='lg'
                  className='px-8'
                >
                  {isFetchingNextPage ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Loading...
                    </>
                  ) : (
                    "Load More Products"
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
